import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../App";

const AttendancePage = () => {
  const [records, setRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [filter, setFilter] = useState({ status: '', employee_id: '', date_from: '', date_to: '' });
  const [formData, setFormData] = useState({
    employee_id: '',
    date: new Date().toISOString().split('T')[0],
    clock_in: '09:00',
    clock_out: '18:00',
    break_start: '13:00',
    break_end: '14:00',
    notes: ''
  });
  const [importData, setImportData] = useState('');
  const { user } = useAuth();

  const isAdmin = ['super_admin', 'tenant_admin', 'hr_manager'].includes(user?.role);

  useEffect(() => {
    fetchRecords();
    if (isAdmin) fetchEmployees();
  }, [filter]);

  const fetchRecords = async () => {
    try {
      const params = new URLSearchParams();
      if (filter.status) params.append('status', filter.status);
      if (filter.employee_id) params.append('employee_id', filter.employee_id);
      if (filter.date_from) params.append('date_from', filter.date_from);
      if (filter.date_to) params.append('date_to', filter.date_to);
      
      const response = await api.get(`/hr/time-records?${params.toString()}`);
      setRecords(response.data);
    } catch (error) {
      console.error('Error fetching records:', error);
      toast.error('Erro ao carregar registos de assiduidade');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees?limit=500');
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleCreateRecord = async (e) => {
    e.preventDefault();
    try {
      await api.post('/hr/time-records', formData);
      toast.success('Registo de ponto criado com sucesso');
      setShowModal(false);
      setFormData({
        employee_id: '',
        date: new Date().toISOString().split('T')[0],
        clock_in: '09:00',
        clock_out: '18:00',
        break_start: '13:00',
        break_end: '14:00',
        notes: ''
      });
      fetchRecords();
    } catch (error) {
      console.error('Error creating record:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar registo');
    }
  };

  const handleValidate = async (recordId, action) => {
    try {
      await api.put(`/hr/time-records/${recordId}/validate?action=${action}`);
      toast.success(action === 'approve' ? 'Registo aprovado' : 'Registo rejeitado');
      fetchRecords();
    } catch (error) {
      console.error('Error validating record:', error);
      toast.error('Erro ao validar registo');
    }
  };

  const handleImport = async (e) => {
    e.preventDefault();
    try {
      const records = JSON.parse(importData);
      const response = await api.post('/hr/time-records/import', records);
      toast.success(`${response.data.imported} registos importados com sucesso`);
      if (response.data.errors > 0) {
        toast.warning(`${response.data.errors} registos com erros`);
      }
      setShowImportModal(false);
      setImportData('');
      fetchRecords();
    } catch (error) {
      console.error('Error importing records:', error);
      toast.error('Erro ao importar registos. Verifique o formato JSON.');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      approved: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30',
      pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30',
      rejected: 'bg-red-100 text-red-700 dark:bg-red-900/30'
    };
    const labels = {
      approved: 'Aprovado',
      pending: 'Pendente',
      rejected: 'Rejeitado'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || colors.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getValidationBadge = (status) => {
    const colors = {
      validated: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30',
      pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30',
      rejected: 'bg-red-100 text-red-700 dark:bg-red-900/30'
    };
    const labels = {
      validated: 'Validado',
      pending: 'Por validar',
      rejected: 'Rejeitado'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || colors.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="attendance-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans']">Assiduidade</h1>
          <p className="text-muted-foreground">Gestão de registos de ponto</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button
              onClick={() => setShowImportModal(true)}
              className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              Importar
            </button>
          )}
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Novo Registo
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-xl border bg-card p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Estado</label>
            <select
              value={filter.status}
              onChange={(e) => setFilter({ ...filter, status: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background"
            >
              <option value="">Todos</option>
              <option value="pending">Pendente</option>
              <option value="approved">Aprovado</option>
              <option value="rejected">Rejeitado</option>
            </select>
          </div>
          {isAdmin && (
            <div>
              <label className="block text-sm font-medium mb-1">Colaborador</label>
              <select
                value={filter.employee_id}
                onChange={(e) => setFilter({ ...filter, employee_id: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-input bg-background"
              >
                <option value="">Todos</option>
                {employees.map(emp => (
                  <option key={emp.employee_id} value={emp.employee_id}>{emp.name}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium mb-1">Data Início</label>
            <input
              type="date"
              value={filter.date_from}
              onChange={(e) => setFilter({ ...filter, date_from: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Data Fim</label>
            <input
              type="date"
              value={filter.date_to}
              onChange={(e) => setFilter({ ...filter, date_to: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background"
            />
          </div>
        </div>
      </div>

      {/* Records Table */}
      <div className="rounded-xl border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium">Data</th>
                {isAdmin && <th className="px-4 py-3 text-left text-sm font-medium">Colaborador</th>}
                <th className="px-4 py-3 text-left text-sm font-medium">Entrada</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Saída</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Pausa</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Total</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Tipo</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Validação</th>
                {isAdmin && <th className="px-4 py-3 text-left text-sm font-medium">Ações</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {records.length === 0 ? (
                <tr>
                  <td colSpan={isAdmin ? 9 : 7} className="px-4 py-8 text-center text-muted-foreground">
                    Sem registos de assiduidade
                  </td>
                </tr>
              ) : (
                records.map((record) => (
                  <tr key={record.record_id} className="hover:bg-muted/30">
                    <td className="px-4 py-3 text-sm">{record.date}</td>
                    {isAdmin && <td className="px-4 py-3 text-sm">{record.employee_id}</td>}
                    <td className="px-4 py-3 text-sm font-medium">{record.clock_in || '-'}</td>
                    <td className="px-4 py-3 text-sm font-medium">{record.clock_out || '-'}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {record.break_start && record.break_end ? `${record.break_start} - ${record.break_end}` : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium">{record.total_hours ? `${record.total_hours}h` : '-'}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs ${record.record_type === 'manual' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
                        {record.record_type === 'manual' ? 'Manual' : record.record_type === 'imported' ? 'Importado' : record.record_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">{getValidationBadge(record.validation_status)}</td>
                    {isAdmin && (
                      <td className="px-4 py-3 text-sm">
                        {record.validation_status === 'pending' && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleValidate(record.record_id, 'approve')}
                              className="p-1 text-emerald-600 hover:bg-emerald-100 rounded"
                              title="Aprovar"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleValidate(record.record_id, 'reject')}
                              className="p-1 text-red-600 hover:bg-red-100 rounded"
                              title="Rejeitar"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Record Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl border shadow-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <h2 className="text-xl font-semibold">Novo Registo de Ponto</h2>
            </div>
            <form onSubmit={handleCreateRecord} className="p-6 space-y-4">
              {isAdmin && (
                <div>
                  <label className="block text-sm font-medium mb-1">Colaborador</label>
                  <select
                    value={formData.employee_id}
                    onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    required={isAdmin}
                  >
                    <option value="">Selecionar colaborador</option>
                    {employees.map(emp => (
                      <option key={emp.employee_id} value={emp.employee_id}>{emp.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1">Data</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Entrada</label>
                  <input
                    type="time"
                    value={formData.clock_in}
                    onChange={(e) => setFormData({ ...formData, clock_in: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Saída</label>
                  <input
                    type="time"
                    value={formData.clock_out}
                    onChange={(e) => setFormData({ ...formData, clock_out: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Início Pausa</label>
                  <input
                    type="time"
                    value={formData.break_start}
                    onChange={(e) => setFormData({ ...formData, break_start: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Fim Pausa</label>
                  <input
                    type="time"
                    value={formData.break_end}
                    onChange={(e) => setFormData({ ...formData, break_end: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Notas</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  rows={2}
                  placeholder="Observações (opcional)"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  Criar Registo
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl border shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <h2 className="text-xl font-semibold">Importar Registos de Assiduidade</h2>
            </div>
            <form onSubmit={handleImport} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Dados JSON</label>
                <textarea
                  value={importData}
                  onChange={(e) => setImportData(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background font-mono text-sm"
                  rows={12}
                  placeholder={`[
  {
    "employee_id": "emp_xxx",
    "date": "2025-01-15",
    "clock_in": "09:00",
    "clock_out": "18:00",
    "break_start": "13:00",
    "break_end": "14:00",
    "total_hours": 8,
    "source": "biometrico"
  }
]`}
                  required
                />
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Formato esperado:</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• <code>employee_id</code>: ID do colaborador (obrigatório)</li>
                  <li>• <code>date</code>: Data no formato YYYY-MM-DD (obrigatório)</li>
                  <li>• <code>clock_in</code>: Hora de entrada HH:MM</li>
                  <li>• <code>clock_out</code>: Hora de saída HH:MM</li>
                  <li>• <code>break_start</code>: Início da pausa HH:MM</li>
                  <li>• <code>break_end</code>: Fim da pausa HH:MM</li>
                  <li>• <code>total_hours</code>: Total de horas trabalhadas</li>
                  <li>• <code>source</code>: Origem do registo (opcional)</li>
                </ul>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => setShowImportModal(false)}
                  className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  Importar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendancePage;
